import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import { TabBar } from './registerTabs';

// Feature gate, not a source toggle — there is no legacy path for this page. Same pattern and reason as
// BPATracker's REACT_APP_BPA_ENABLED: the nav item has shown a placeholder since v1, so an un-flipped
// environment keeps showing exactly that rather than a half-wired screen.
const CONVERSION_ENABLED = process.env.REACT_APP_CONVERSION_ENABLED === 'true';

const TABS = [
  { key: 'streams', label: 'Streams' },
  { key: 'deliveries', label: 'Deliveries' },
  { key: 'matrix', label: 'Module matrix' },
  { key: 'issues', label: 'Issues' },
];

const ITERATION_BADGE = {
  'Not Started': 'badge-gray',
  'In Progress': 'badge-blue',
  Delivered: 'badge-violet',
  Validated: 'badge-yellow',
  Accepted: 'badge-green',
  Canceled: 'badge-gray',
};

const ISSUE_BADGE = {
  Open: 'badge-red',
  'In Progress': 'badge-blue',
  Blocked: 'badge-red',
  Resolved: 'badge-green',
  Closed: 'badge-gray',
  Deferred: 'badge-yellow',
};

const SEVERITY_BADGE = {
  Critical: 'badge-red',
  High: 'badge-yellow',
  Medium: 'badge-blue',
  Low: 'badge-gray',
};

// Shades a matrix cell by how far along it is. Deliberately not a badge — the grid is read as a whole,
// and seven badges per row is noise where a color gradient is a glance.
const STAGE_TINT = {
  'Not Started': { background: 'var(--white)', color: 'var(--text-light)' },
  Mapped: { background: 'rgba(47,147,177,0.10)', color: 'var(--primary-dark)' },
  Converted: { background: 'rgba(47,147,177,0.22)', color: 'var(--primary-dark)' },
  Balanced: { background: 'rgba(0,124,100,0.15)', color: 'var(--green)' },
  Validated: { background: 'rgba(0,124,100,0.30)', color: 'var(--green)' },
  'Not Applicable': { background: 'var(--light-bg)', color: 'var(--text-light)' },
};

/** Date input wants `yyyy-mm-dd`; the API returns an ISO timestamp. Same helper BPATracker has. */
export function toDateInput(value) {
  return value ? String(value).slice(0, 10) : '';
}

/**
 * Slipped when the actual delivery landed after the scheduled one, or when a scheduled date is in the
 * past and nothing has been delivered.
 *
 * Compared as `yyyy-mm-dd` strings rather than Dates: both values are date-only (ADR 0009) and a string
 * compare cannot pick up a timezone offset on the way through, which is the failure mode that put ADR
 * 0009 in the repo in the first place.
 */
export function deliverySlip(iteration, today) {
  const scheduled = toDateInput(iteration.scheduledDelivery);
  if (!scheduled) return null;
  const actual = toDateInput(iteration.actualDelivery);
  if (actual) return actual > scheduled ? 'late' : null;
  return scheduled < today ? 'overdue' : null;
}

/**
 * The furthest-behind stage across a set of cells, which is what "where is this delivery" actually means
 * — a delivery is not balanced because five of seven modules are.
 *
 * `Not Applicable` is skipped rather than ranked: a module with no data to convert is not behind, and
 * giving it a rank would drag the answer toward a value that means nothing. Mirrors why
 * CONVERSION_STAGE_SEQUENCE omits it.
 */
export function furthestBehind(cells, stageSequence) {
  const ranked = cells
    .map((c) => c.stage)
    .filter((stage) => stageSequence[stage] !== undefined);
  if (!ranked.length) return null;
  return ranked.reduce((worst, stage) =>
    stageSequence[stage] < stageSequence[worst] ? stage : worst,
  );
}

// "Not ours" is every non-Aumentum owner, Third Party included — the split that matters is who is
// holding the delivery up, which is what the data-cleansing argument with a customer turns on.
export function bucketIssues(issues, openStatuses = []) {
  const isOpen = (issue) => openStatuses.includes(issue.status);
  const open = issues.filter(isOpen);
  return {
    open,
    ours: open.filter((i) => i.owner === 'Aumentum'),
    theirs: open.filter((i) => i.owner !== 'Aumentum'),
    closed: issues.filter((i) => !isOpen(i)),
  };
}

function Badge({ value, map }) {
  if (!value) return <span className="cell-read">—</span>;
  return <span className={`badge ${map[value] ?? 'badge-gray'}`}>{value}</span>;
}

function StatCard({ label, value, tone }) {
  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 6 }}>{label}</div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          color: tone || 'var(--primary-dark)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ---------- Streams ----------

function StreamsTab({ streams, vocabulary, onRefresh, setError, setNotice }) {
  const [busy, setBusy] = useState('');
  const [retiring, setRetiring] = useState(null);
  const [retireReason, setRetireReason] = useState('');
  const [supersededBy, setSupersededBy] = useState('');

  const seed = async (stream) => {
    setBusy(stream.id);
    setError('');
    setNotice('');
    try {
      const result = await apiFetch(`/conversion/seed/${stream.id}`, { method: 'POST' });
      // The unclaimed list is the point of the response, not a footnote: a conversion task no delivery
      // took is exactly what the template cannot name, and silence would read as "nothing was missed".
      const unclaimed = result.unclaimedTasks.length
        ? ` ${result.unclaimedTasks.length} conversion task(s) went unclaimed: ${result.unclaimedTasks
            .map((t) => t.title)
            .join(', ')}.`
        : '';
      // A contested task is worse than an unclaimed one: both streams render the same dates and look
      // right. Named here because nothing else on the page can show it.
      // One row per (task, rival stream), so this counts claims — a task two other streams took is two.
      const contested = result.contestedTasks?.length
        ? ` ${result.contestedTasks.length} claim(s) on these tasks by another stream: ${result.contestedTasks
            .map((t) => `${t.title} (also on "${t.streamName}")`)
            .join(', ')}.`
        : '';
      setNotice(
        `${result.created.length} delivery(ies) created, ${result.alreadyPresent.length} already there, ` +
          `${result.cellsCreated} matrix cell(s) added for ${result.modulesInScope.length} module(s).` +
          `${unclaimed}${contested}`,
      );
      await onRefresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const retire = async () => {
    setBusy(retiring.id);
    setError('');
    setNotice('');
    try {
      await apiFetch(`/conversion-streams/${retiring.id}/retire`, {
        method: 'POST',
        body: JSON.stringify({
          ...(retireReason ? { reason: retireReason } : {}),
          ...(supersededBy ? { supersededByStreamId: supersededBy } : {}),
        }),
      });
      setNotice(`"${retiring.name}" retired. Its deliveries, matrix cells and issues are kept.`);
      setRetiring(null);
      setRetireReason('');
      setSupersededBy('');
      await onRefresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const reactivate = async (stream) => {
    setBusy(stream.id);
    setError('');
    try {
      await apiFetch(`/conversion-streams/${stream.id}/reactivate`, { method: 'POST' });
      setNotice(`"${stream.name}" is active again.`);
      await onRefresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  if (!streams.length) {
    return (
      <div className="card">
        <div className="card-title">No conversion streams yet</div>
        <p className="page-subtitle">
          A stream is one legacy system moving to one target, with one treatment — a project runs as many
          as it needs. Add the first one to start recording deliveries.
        </p>
      </div>
    );
  }

  const activeStreams = streams.filter((s) => s.isActive);

  return (
    <>
      <div className="sheet-wrap">
        <table className="sheet">
          <thead>
            <tr>
              <th style={{ minWidth: 260 }}>Stream</th>
              <th>Legacy system</th>
              <th>Target</th>
              <th>Treatment</th>
              <th>System of record</th>
              <th className="cell-num">Deliveries</th>
              <th style={{ minWidth: 200 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {streams.map((stream, index) => (
              <tr key={stream.id} className={index % 2 ? 'is-alt' : undefined}>
                <td className="cell-read">
                  <div style={{ fontWeight: 600, color: 'var(--text-dark)' }}>{stream.name}</div>
                  {!stream.isActive && (
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      <span className="badge badge-gray">Retired</span>{' '}
                      {stream.retiredReason && <span>{stream.retiredReason}</span>}
                      {stream.supersededBy && <span> → folded into {stream.supersededBy.name}</span>}
                    </div>
                  )}
                  {stream.notes && stream.isActive && (
                    <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>
                      {stream.notes}
                    </div>
                  )}
                </td>
                <td className="cell-read">
                  {vocabulary?.legacySystemLabels?.[stream.legacySystem] ?? stream.legacySystem}
                  {stream.legacySystemName && ` — ${stream.legacySystemName}`}
                </td>
                <td className="cell-read">{stream.targetProduct ?? '—'}</td>
                <td className="cell-read">{stream.treatment}</td>
                <td className="cell-read">
                  {/* The field that answers "why has this module never reached Validated?". Labeled, not
                      left as a bare boolean, because that question is the reason it exists. */}
                  {stream.isSystemOfRecord ? (
                    <span className="badge badge-green">Ours</span>
                  ) : (
                    <span className="badge badge-yellow" title="Aumentum is not taking this data over as system of record, so balancing it to legacy totals may be the wrong test">
                      Not ours
                    </span>
                  )}
                </td>
                <td className="cell-num">{stream.iterations?.length ?? 0}</td>
                <td className="cell-read">
                  {stream.isActive ? (
                    <>
                      {stream.treatment !== 'Ongoing interface' && (
                        <button
                          className="btn btn-outline"
                          disabled={busy === stream.id}
                          onClick={() => seed(stream)}
                          style={{ marginRight: 8 }}
                        >
                          {stream.iterations?.length ? 'Re-seed' : 'Seed deliveries'}
                        </button>
                      )}
                      <button
                        className="btn btn-outline"
                        disabled={busy === stream.id}
                        onClick={() => setRetiring(stream)}
                      >
                        Retire…
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-outline"
                      disabled={busy === stream.id}
                      onClick={() => reactivate(stream)}
                    >
                      Reactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {retiring && (
        <div className="card" style={{ marginTop: 18 }}>
          <div className="card-title">Retire “{retiring.name}”</div>
          <p className="page-subtitle" style={{ marginBottom: 14 }}>
            The stream drops out of the default view. Its deliveries, matrix cells and issues are all
            kept — this is how a descoped stream is recorded, not deleted.
          </p>
          <div className="form-group">
            <input
              className="form-input"
              placeholder="Why is it being dropped? (e.g. folded into the upgrade stream at project reset)"
              value={retireReason}
              onChange={(e) => setRetireReason(e.target.value)}
            />
          </div>
          <div className="form-group">
            <select
              className="form-select"
              value={supersededBy}
              onChange={(e) => setSupersededBy(e.target.value)}
            >
              <option value="">Not folded into another stream</option>
              {activeStreams
                .filter((s) => s.id !== retiring.id)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    Folded into: {s.name}
                  </option>
                ))}
            </select>
          </div>
          <button className="btn btn-primary" disabled={busy === retiring.id} onClick={retire}>
            Retire stream
          </button>
          <button
            className="btn btn-outline"
            style={{ marginLeft: 8 }}
            onClick={() => setRetiring(null)}
          >
            Cancel
          </button>
        </div>
      )}
    </>
  );
}

// ---------- Deliveries ----------

function DeliveriesTab({ streams, iterations, today }) {
  if (!iterations.length) {
    return (
      <div className="card">
        <div className="card-title">No deliveries recorded</div>
        <p className="page-subtitle">
          Seed a stream from the Streams tab — that creates its delivery set from the standard template
          and links each one to the conversion task already in the project schedule.
        </p>
      </div>
    );
  }

  // Grouped by stream, because a delivery number only means something within its own stream.
  const byStream = streams
    .map((stream) => ({
      stream,
      rows: iterations.filter((it) => it.stream?.id === stream.id),
    }))
    .filter((g) => g.rows.length);

  return (
    <>
      {byStream.map(({ stream, rows }) => (
        <div key={stream.id} style={{ marginBottom: 24 }}>
          <div className="card-title" style={{ marginBottom: 10 }}>
            {stream.name}{' '}
            <span className="badge badge-gray" style={{ marginLeft: 6 }}>
              {stream.treatment}
            </span>
            {!stream.isActive && (
              <span className="badge badge-gray" style={{ marginLeft: 6 }}>
                Retired
              </span>
            )}
          </div>
          <div className="sheet-wrap">
            <table className="sheet">
              <thead>
                <tr>
                  <th className="cell-num">#</th>
                  <th style={{ minWidth: 200 }}>Delivery</th>
                  <th>Phase</th>
                  <th>Status</th>
                  <th>Scheduled</th>
                  <th>Actual</th>
                  <th>Platform release</th>
                  <th style={{ minWidth: 220 }}>Schedule task</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((it, index) => {
                  const slip = deliverySlip(it, today);
                  return (
                    <tr key={it.id} className={index % 2 ? 'is-alt' : undefined}>
                      <td className="cell-num">{it.sequence}</td>
                      <td className="cell-read" style={{ fontWeight: 600, color: 'var(--text-dark)' }}>
                        {it.name}
                      </td>
                      {/* The canonical phase, so this timeline and the methodology's are one timeline. */}
                      <td className="cell-read">{it.phaseTitle ?? '—'}</td>
                      <td className="cell-read">
                        <Badge value={it.status} map={ITERATION_BADGE} />
                      </td>
                      <td className="cell-read">{toDateInput(it.scheduledDelivery) || '—'}</td>
                      <td className="cell-read">
                        {toDateInput(it.actualDelivery) || '—'}
                        {slip && (
                          <span
                            className={`badge ${slip === 'late' ? 'badge-yellow' : 'badge-red'}`}
                            style={{ marginLeft: 6 }}
                          >
                            {slip === 'late' ? 'Late' : 'Overdue'}
                          </span>
                        )}
                      </td>
                      {/* Slipping the minimum release is a real risk nothing else in the app surfaces. */}
                      <td className="cell-read">{it.platformRelease ?? '—'}</td>
                      <td className="cell-read">
                        {it.task ? (
                          <>
                            {it.task.title}
                            <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
                              {toDateInput(it.task.startDate)} → {toDateInput(it.task.endDate)}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: 'var(--text-light)' }}>Not linked</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
}

// ---------- Module matrix ----------

function MatrixTab({ streams, iterations, progress, vocabulary, onSaved, setError, setNotice }) {
  const [streamId, setStreamId] = useState('');
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState(false);

  const seededStreams = useMemo(
    () => streams.filter((s) => (s.iterations?.length ?? 0) > 0),
    [streams],
  );

  // Default to the first stream that actually has deliveries — the axes come from them. The
  // validity check also recovers a selection whose stream was retired, now that a save keeps this
  // component mounted rather than remounting it with fresh state.
  useEffect(() => {
    if (!seededStreams.length) return;
    if (!streamId || !seededStreams.some((s) => s.id === streamId)) setStreamId(seededStreams[0].id);
  }, [streamId, seededStreams]);

  const columns = useMemo(
    () => iterations.filter((it) => it.stream?.id === streamId),
    [iterations, streamId],
  );

  // Rows are whatever modules have cells in this stream. Derived from the cells rather than from the
  // project's module list on purpose: seeding already scoped them to the stream's target product, and
  // re-deriving that here would be a second implementation of the same rule.
  const rows = useMemo(() => {
    const cells = progress.filter((c) => columns.some((col) => col.id === c.iterationId));
    const seen = new Map();
    for (const cell of cells) {
      if (!seen.has(cell.module.id)) seen.set(cell.module.id, cell.module);
    }
    return [...seen.values()].sort((a, b) => (a.sequence ?? 99) - (b.sequence ?? 99));
  }, [progress, columns]);

  const cellFor = (iterationId, moduleId) =>
    progress.find((c) => c.iterationId === iterationId && c.module.id === moduleId);

  const stageOf = (iterationId, moduleId) => {
    const key = `${iterationId}:${moduleId}`;
    if (dirty[key] !== undefined) return dirty[key];
    return cellFor(iterationId, moduleId)?.stage ?? 'Not Started';
  };

  const save = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const rowsToSend = Object.entries(dirty).map(([key, stage]) => {
        const [iterationId, moduleId] = key.split(':');
        return { iterationId, moduleId, stage };
      });
      await apiFetch('/conversion-progress', {
        method: 'PATCH',
        body: JSON.stringify({ rows: rowsToSend }),
      });
      setNotice(`${rowsToSend.length} cell(s) saved.`);
      setDirty({});
      await onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!seededStreams.length) {
    return (
      <div className="card">
        <div className="card-title">Nothing to show yet</div>
        <p className="page-subtitle">
          The matrix is modules × deliveries for one stream. Seed a stream from the Streams tab first.
        </p>
      </div>
    );
  }

  const stages = vocabulary?.stages ?? [];
  const stageSequence = vocabulary?.stageSequence ?? {};
  const dirtyCount = Object.keys(dirty).length;

  return (
    <>
      <div className="sheet-toolbar">
        <select
          className="form-select"
          style={{ maxWidth: 380 }}
          value={streamId}
          onChange={(e) => {
            setStreamId(e.target.value);
            setDirty({});
          }}
          aria-label="Stream"
        >
          {seededStreams.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <div className="spacer" />
        {dirtyCount > 0 && (
          <>
            <span className="cell-read" style={{ padding: 0, fontSize: 12 }}>
              {dirtyCount} unsaved change(s)
            </span>
            <button className="btn btn-outline" onClick={() => setDirty({})}>
              Discard
            </button>
            <button className="btn btn-primary" disabled={busy} onClick={save}>
              Save changes
            </button>
          </>
        )}
      </div>

      <div className="sheet-wrap">
        <table className="sheet">
          <thead>
            <tr>
              <th style={{ minWidth: 200, position: 'sticky', left: 0, zIndex: 3 }}>Module</th>
              {columns.map((col) => (
                <th key={col.id} className="cell-month" style={{ minWidth: 130 }}>
                  {col.name}
                  <div style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-light)' }}>
                    {toDateInput(col.scheduledDelivery) || 'unscheduled'}
                  </div>
                </th>
              ))}
              <th style={{ minWidth: 120 }}>Furthest behind</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((module, index) => {
              const rowCells = columns.map((col) => ({ stage: stageOf(col.id, module.id) }));
              const worst = furthestBehind(rowCells, stageSequence);
              return (
                <tr key={module.id} className={index % 2 ? 'is-alt' : undefined}>
                  <td
                    className="cell-read is-frozen is-frozen-edge"
                    style={{ left: 0, fontWeight: 600, color: 'var(--text-dark)' }}
                  >
                    {module.name}
                    <div style={{ fontSize: 11, color: 'var(--text-light)' }}>{module.code}</div>
                  </td>
                  {columns.map((col) => {
                    const key = `${col.id}:${module.id}`;
                    const stage = stageOf(col.id, module.id);
                    return (
                      <td key={col.id} className="cell-month" style={STAGE_TINT[stage]}>
                        <select
                          value={stage}
                          onChange={(e) =>
                            setDirty((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                          style={{
                            width: '100%',
                            border: 'none',
                            background: 'transparent',
                            padding: '7px 8px',
                            font: 'inherit',
                            color: 'inherit',
                            cursor: 'pointer',
                          }}
                          aria-label={`${module.name} — ${col.name}`}
                        >
                          {stages.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                    );
                  })}
                  <td className="cell-read">{worst ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="sheet-note">
        A cell is the stage one module reached in one delivery. <strong>Furthest behind</strong> is the
        honest answer to “where is this module” — a delivery is not balanced because most of its modules
        are. <em>Not Applicable</em> is excluded from that, since a module with no data to convert is not
        behind.
      </div>
    </>
  );
}

// ---------- Issues ----------

function IssuesTab({ issues, vocabulary }) {
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('');

  const filtered = useMemo(
    () =>
      issues.filter(
        (i) => (!category || i.category === category) && (!severity || i.severity === severity),
      ),
    [issues, category, severity],
  );

  const { open, ours, theirs, closed } = useMemo(
    () => bucketIssues(filtered, vocabulary?.openIssueStatuses ?? []),
    [filtered, vocabulary],
  );

  const table = (rows, title, tone) => (
    <div style={{ flex: '1 1 420px', minWidth: 380 }}>
      <div className="card-title" style={{ marginBottom: 10 }}>
        {title} <span className={`badge ${tone}`}>{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="card" style={{ marginBottom: 0 }}>
          <span className="page-subtitle">Nothing here.</span>
        </div>
      ) : (
        <div className="sheet-wrap">
          <table className="sheet">
            <thead>
              <tr>
                <th style={{ minWidth: 200 }}>Issue</th>
                <th>Category</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Reported</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((issue, index) => (
                <tr key={issue.id} className={index % 2 ? 'is-alt' : undefined}>
                  <td className="cell-read">
                    <div style={{ fontWeight: 600, color: 'var(--text-dark)' }}>{issue.title}</div>
                    {issue.assignedTo && (
                      <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
                        {issue.assignedTo}
                      </div>
                    )}
                    {issue.module && (
                      <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
                        {issue.module.code}
                        {issue.iteration && ` · ${issue.iteration.name}`}
                      </div>
                    )}
                  </td>
                  <td className="cell-read">{issue.category}</td>
                  <td className="cell-read">
                    <Badge value={issue.severity} map={SEVERITY_BADGE} />
                  </td>
                  <td className="cell-read">
                    <Badge value={issue.status} map={ISSUE_BADGE} />
                  </td>
                  <td className="cell-read">{toDateInput(issue.reportedDate) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Counted off `filtered`, not the project-wide summary endpoint: a card that disagrees with
          the table beneath it is read aloud in triage calls and costs the page its credibility. */}
      <div className="stat-row">
        <StatCard label="Open — ours" value={ours.length} />
        <StatCard label="Open — not ours" value={theirs.length} tone="var(--yellow)" />
        <StatCard label="Open — total" value={open.length} />
        <StatCard label="All issues" value={filtered.length} />
      </div>

      <div className="sheet-toolbar">
        <select
          className="form-select"
          style={{ maxWidth: 220 }}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          aria-label="Category"
        >
          <option value="">All categories</option>
          {(vocabulary?.issueCategories ?? []).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          className="form-select"
          style={{ maxWidth: 180 }}
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          aria-label="Severity"
        >
          <option value="">All severities</option>
          {(vocabulary?.issueSeverities ?? []).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 24 }}>
        {table(ours, 'Open — ours', 'badge-blue')}
        {table(theirs, 'Open — not ours', 'badge-yellow')}
      </div>

      {closed.length > 0 && table(closed, 'Closed and resolved', 'badge-gray')}
    </>
  );
}

// ---------- Page ----------

function DataConversion() {
  const [tab, setTab] = useState('streams');
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [vocabulary, setVocabulary] = useState(null);
  const [streams, setStreams] = useState([]);
  const [iterations, setIterations] = useState([]);
  const [progress, setProgress] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedProjectId, setLoadedProjectId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Computed once per render rather than per row — `deliverySlip` is called for every delivery and a
  // fresh Date each time would be both wasteful and capable of straddling midnight mid-render.
  const today = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate(),
    ).padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [projectList, vocab] = await Promise.all([
          apiFetch('/projects'),
          apiFetch('/conversion/vocabulary'),
        ]);
        setProjects(projectList);
        setVocabulary(vocab);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, []);

  const load = useCallback(async (id) => {
    if (!id) {
      setStreams([]);
      setIterations([]);
      setProgress([]);
      setIssues([]);
      setLoadedProjectId('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // includeRetired so the Streams tab can show a dropped stream's record. Every other tab filters
      // on what it needs from the same payload rather than re-fetching per tab.
      const [streamList, iterationList, progressList, issueList] = await Promise.all([
        apiFetch(`/conversion-streams?projectId=${id}&includeRetired=true`),
        apiFetch(`/conversion-iterations?projectId=${id}`),
        apiFetch(`/conversion-progress?projectId=${id}`),
        apiFetch(`/conversion-issues?projectId=${id}`),
      ]);
      setStreams(streamList);
      setIterations(iterationList);
      setProgress(progressList);
      setIssues(issueList);
      setLoadedProjectId(id);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(projectId);
  }, [projectId, load]);

  const refresh = useCallback(() => load(projectId), [load, projectId]);

  if (!CONVERSION_ENABLED) {
    return (
      <div className="placeholder-page">
        <div className="placeholder-icon">🔄</div>
        <div className="placeholder-title">Data Conversion</div>
        <div className="placeholder-sub">
          Built, but not switched on in this environment. Set{' '}
          <code>REACT_APP_CONVERSION_ENABLED=true</code> to enable it.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Project Implementation</div>
        <h1 className="page-title">Data Conversion</h1>
        <p className="page-subtitle">
          Conversion streams and their deliveries, how far each module got in each one, and the issue log
          that replaces the conversion tracking spreadsheet.
        </p>
      </div>

      {error && <div className="notice notice-error">{error}</div>}
      {notice && <div className="notice notice-info">{notice}</div>}

      <div className="card">
        <div className="sheet-toolbar" style={{ marginBottom: 0 }}>
          <select
            className="form-select"
            style={{ maxWidth: 340 }}
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            aria-label="Project"
          >
            <option value="">Select a project…</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <div className="spacer" />
          {projectId && (
            <span className="cell-read" style={{ padding: 0, fontSize: 12 }}>
              {streams.filter((s) => s.isActive).length} active stream(s), {iterations.length}{' '}
              delivery(ies)
            </span>
          )}
        </div>
      </div>

      {loading && <div className="notice notice-info">Loading…</div>}

      {projectId && loadedProjectId === projectId && (
        <>
          {/* Not `merge`: each tab renders its own separate card(s) below, not one panel to attach to. */}
          <TabBar tabs={TABS} active={tab} onSelect={setTab} />

          {tab === 'streams' && (
            <StreamsTab
              streams={streams}
              vocabulary={vocabulary}
              onRefresh={refresh}
              setError={setError}
              setNotice={setNotice}
            />
          )}
          {tab === 'deliveries' && (
            <DeliveriesTab streams={streams} iterations={iterations} today={today} />
          )}
          {tab === 'matrix' && (
            <MatrixTab
              streams={streams}
              iterations={iterations}
              progress={progress}
              vocabulary={vocabulary}
              onSaved={refresh}
              setError={setError}
              setNotice={setNotice}
            />
          )}
          {tab === 'issues' && (
            <IssuesTab issues={issues} vocabulary={vocabulary} />
          )}
        </>
      )}
    </div>
  );
}

export default DataConversion;
